/**
 * Browser console script to clean up IndexedDB pending entries
 * Copy and paste this into the browser console to run
 */

import logger from '../core/logger';

export async function cleanupIndexedDBNow() {
  logger.debug('🔍 Checking IndexedDB for invalid entries...');

  // Import localforage
  const localforage = (await import('localforage')).default;

  const entryStore = localforage.createInstance({
    name: 'journal',
    storeName: 'pendingEntries',
  });

  try {
    const pendingEntries = await entryStore.getItem('entries') || [];
    logger.debug(`📦 Found ${pendingEntries.length} pending entries in IndexedDB`);

    if (pendingEntries.length === 0) {
      logger.debug('✅ No pending entries to clean');
      return { deleted: 0, remaining: 0 };
    }

    // Log all pending entries
    logger.debug('Pending entries:', pendingEntries);

    const invalidEntries = [];
    const validEntries = [];

    pendingEntries.forEach((entry, index) => {
      const createdAt = entry.createdAt;
      const id = entry._tempId || entry.id || `entry-${index}`;

      logger.debug(`\nChecking entry ${index + 1}:`);
      logger.debug('  ID:', id);
      logger.debug('  createdAt:', createdAt);
      logger.debug('  Content preview:', (entry.content || entry.extractedText || 'No content').substring(0, 50));

      if (!createdAt) {
        logger.debug('  ❌ INVALID: Missing createdAt');
        invalidEntries.push({ ...entry, _index: index, _reason: 'Missing createdAt' });
      } else {
        try {
          const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
          if (isNaN(date.getTime())) {
            logger.debug('  ❌ INVALID: Bad timestamp');
            invalidEntries.push({ ...entry, _index: index, _reason: 'Invalid timestamp' });
          } else {
            logger.debug('  ✅ VALID');
            validEntries.push(entry);
          }
        } catch (error) {
          logger.debug('  ❌ INVALID: Parse error');
          invalidEntries.push({ ...entry, _index: index, _reason: 'Parse error' });
        }
      }
    });

    logger.debug(`\n📊 Results:`);
    logger.debug(`  Valid entries: ${validEntries.length}`);
    logger.debug(`  Invalid entries: ${invalidEntries.length}`);

    if (invalidEntries.length === 0) {
      logger.debug('\n✅ All pending entries are valid!');
      return { deleted: 0, remaining: validEntries.length };
    }

    logger.debug('\n⚠️  Invalid entries to delete:');
    invalidEntries.forEach((entry, i) => {
      logger.debug(`  ${i + 1}. ${entry._tempId || entry.id} - ${entry._reason}`);
    });

    // Delete invalid entries
    await entryStore.setItem('entries', validEntries);

    logger.debug(`\n✅ Deleted ${invalidEntries.length} invalid entries from IndexedDB`);
    logger.debug(`✅ ${validEntries.length} valid entries remaining`);

    return {
      deleted: invalidEntries.length,
      remaining: validEntries.length,
      invalidEntries: invalidEntries.map(e => ({
        id: e._tempId || e.id,
        reason: e._reason
      }))
    };

  } catch (error) {
    logger.error('❌ Error during IndexedDB cleanup:', error);
    throw error;
  }
}

// Auto-run if in browser
if (typeof window !== 'undefined') {
  window.cleanupIndexedDBNow = cleanupIndexedDBNow;
  logger.debug('✅ Cleanup function loaded. Run: cleanupIndexedDBNow()');
}
